import React, { useRef, useEffect, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useFetcher, useNavigate } from '@remix-run/react'
// import { Protocol } from 'pmtiles'
import { Slider } from '#app/components/ui/slider.tsx'
import mapData from '#other/mapa.json'
import { Button } from '#app/components/ui/button.tsx'
import Trip from '#app/components/trip.tsx'
import { formatState, formatText } from '#app/utils/format-text.tsx'
import {
	ChevronUp,
	ChevronDown,
	Locate,
	Battery,
	Gauge,
	Zap,
	LocateOff,
	Building,
	Star,
	Loader2,
	User, // Add User icon
} from 'lucide-react'
import { createRoot } from 'react-dom/client'
import MaplibreGeocoder from '@maplibre/maplibre-gl-geocoder'
import '@maplibre/maplibre-gl-geocoder/dist/maplibre-gl-geocoder.css'
import './custom-geocoder.css'

import { useSearchParams } from '@remix-run/react'
import DistanceMeasurementMapLibreGlDirections, {
	config,
} from '#app/components/distance-measurement-directions.ts'
import { Link } from '@remix-run/react'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import { Progress } from '#app/components/ui/progress.tsx'

import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { AutoComplete } from '#app/components/ui/autocomplete.tsx'

// Check for touch support to identify mobile/touch devices
const isMobileDevice =
	typeof window !== 'undefined' &&
	('ontouchstart' in window || navigator.maxTouchPoints > 0)

const decode = (encoded) => {
	const salt = 'bkWUdYqNoedWDz9RZhZL'
	const data = atob(encoded)
	const value = parseFloat(data.replace(salt, ''))
	return value
}

class CustomMaplibreGeocoder extends MaplibreGeocoder {
	_renderNoResults() {
		const errorMessage =
			"<div class='custom-color mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results maplibre-gl-geocoder--error maplibre-gl-geocoder--no-results'>Nenhum resultado encontrado</div>"
		this._renderMessage(errorMessage)
	}
}

export default function MapaPlanejador({
	favorites,
	userCars,
	chargingStations,
	userLat,
	userLon,
	zoomLevel,
}) {
	class RadioFilterControl {
		onAdd(map) {
			this._map = map
			this._container = document.createElement('div')
			this._container.className = 'maplibregl-ctrl'

			const container = this._container
			const root = createRoot(container) // Create a root.

			//current power_filter query string
			const searchParams = new URLSearchParams(window.location.search)
			const powerFilter = searchParams.get('power_filter')

			root.render(
				<RadioFilter
					currentPowerFilter={powerFilter}
					onChange={this.handleFilterChange}
				/>,
			)

			return this._container
		}

		handleFilterChange = (e) => {
			const searchParams = new URLSearchParams(window.location.search)
			if (!e.target.value) {
				searchParams.delete('power_filter')
			} else {
				searchParams.set('power_filter', e.target.value)
			}
			window.history.pushState(
				{},
				'',
				`${window.location.pathname}?${searchParams.toString()}`,
			)

			// Clear the loaded stations cache when filter changes
			setLoadedStations(new Map()) // <-- Reset cache here

			// Trigger a new fetch by updating the filter state
			setFilter(e.target.value || 0)
		}

		onRemove() {
			this._container.parentNode.removeChild(this._container)
			this._map = undefined
		}
	}

	class RouteControl {
		onAdd(map) {
			this._map = map
			this._container = document.createElement('div')
			this._container.className = 'maplibregl-ctrl'

			const button = document.createElement('button')
			button.type = 'button'
			button.className = 'maplibregl-ctrl-route-planner'
			button.setAttribute(
				'aria-label',
				routeStations.length > 0 ? 'Detalhes da Rota' : 'Planejar Viagem',
			)

			// Create SVG element
			const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
			svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
			svg.setAttribute('viewBox', '0 0 24 24')
			svg.setAttribute('fill', 'none')
			svg.setAttribute('stroke', 'currentColor')
			svg.setAttribute('stroke-width', '2')
			svg.setAttribute('stroke-linecap', 'round')
			svg.setAttribute('stroke-linejoin', 'round')

			// Create SVG elements
			const circle1 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'circle',
			)
			circle1.setAttribute('cx', '12')
			circle1.setAttribute('cy', '4.5')
			circle1.setAttribute('r', '2.5')

			const path1 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'path',
			)
			path1.setAttribute('d', 'm10.2 6.3-3.9 3.9')

			const circle2 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'circle',
			)
			circle2.setAttribute('cx', '4.5')
			circle2.setAttribute('cy', '12')
			circle2.setAttribute('r', '2.5')

			const path2 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'path',
			)
			path2.setAttribute('d', 'M7 12h10')

			const circle3 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'circle',
			)
			circle3.setAttribute('cx', '19.5')
			circle3.setAttribute('cy', '12')
			circle3.setAttribute('r', '2.5')

			const path3 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'path',
			)
			path3.setAttribute('d', 'm13.8 17.7 3.9-3.9')

			const circle4 = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'circle',
			)
			circle4.setAttribute('cx', '12')
			circle4.setAttribute('cy', '19.5')
			circle4.setAttribute('r', '2.5')

			// Append all elements to SVG
			svg.appendChild(circle1)
			svg.appendChild(path1)
			svg.appendChild(circle2)
			svg.appendChild(path2)
			svg.appendChild(circle3)
			svg.appendChild(path3)
			svg.appendChild(circle4)

			// Create a span for the text
			const textSpan = document.createElement('span')
			textSpan.textContent =
				routeStations.length > 0 ? 'Detalhes da Rota' : 'Planejar Viagem'

			// Append SVG and text to the button
			button.appendChild(svg)
			button.appendChild(textSpan)

			button.onclick = () => {
				setRouteModalOpen(true)
			}

			this._container.appendChild(button)
			return this._container
		}

		onRemove() {
			this._container.parentNode.removeChild(this._container)
			this._map = undefined
		}
	}

	class FavoritesControl {
		onAdd(map) {
			this._map = map
			this._container = document.createElement('div')
			this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group'

			const button = document.createElement('button')
			button.type = 'button'
			button.setAttribute('aria-label', 'Ver apenas favoritas')
			button.setAttribute('title', 'Ver apenas favoritas')

			// Create root for React component
			const root = createRoot(button)

			// Render the Star icon with dynamic styling
			root.render(
				<Star
					className="h-4 w-4"
					style={{
						fill: this._showingFavorites ? 'currentColor' : 'none',
						transition: 'fill 0.2s ease',
					}}
				/>,
			)

			// Update button styles
			Object.assign(button.style, {
				padding: '6px',
				backgroundColor: '#fff',
				border: 'none',
				borderRadius: '3px',
				cursor: 'pointer',
				color: '#000',
				boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
				width: '30px',
				height: '30px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			})

			// Add hover effect
			button.onmouseover = () => {
				button.style.backgroundColor = '#f2f2f2'
			}
			button.onmouseout = () => {
				button.style.backgroundColor = '#fff'
			}

			this._showingFavorites = false
			button.onclick = () => {
				this._showingFavorites = !this._showingFavorites

				// Update the star fill
				root.render(
					<Star
						className="h-4 w-4"
						style={{
							fill: this._showingFavorites ? 'currentColor' : 'none',
							transition: 'fill 0.2s ease',
						}}
					/>,
				)

				// Update the map filters
				const layers = [
					'stations-vermelho',
					'stations-amarelo',
					'stations-verde',
					'stations-azul',
					'stations-roxo',
				] // Updated layer list

				layers.forEach((layer) => {
					const color = layer.split('-')[1] // e.g., 'vermelho', 'roxo'
					const miniColor = `minis-${color}`

					if (this._showingFavorites) {
						// Show only favorite stations (roxo/minis-roxo)
						if (layer === 'stations-roxo') {
							map.setFilter(layer, [
								'in',
								['get', 'icon'],
								['literal', ['roxo', 'minis-roxo']],
							])
						} else {
							// Hide other color layers
							map.setFilter(layer, ['==', ['get', 'icon'], 'none']) // Filter that matches nothing
						}
					} else {
						// Reset to original filters for each color
						map.setFilter(layer, [
							'in',
							['get', 'icon'],
							['literal', [color, miniColor]],
						])
					}
				})
			}

			this._container.appendChild(button)
			return this._container
		}

		onRemove() {
			this._container.parentNode.removeChild(this._container)
			this._map = undefined
		}
	}

	// New Control for "My Reviews"
	class MyReviewsControl {
		onAdd(map) {
			this._map = map
			this._container = document.createElement('div')
			this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group'

			const button = document.createElement('button')
			button.type = 'button'
			button.setAttribute('aria-label', 'Ver apenas meus check-ins')
			button.setAttribute('title', 'Ver apenas meus check-ins')

			// Create root for React component
			const root = createRoot(button)

			// Initial state from query string
			const searchParams = new URLSearchParams(window.location.search)
			this._showingMyCheckins = searchParams.get('meus_checkins') === 'true'

			// Render the User icon with dynamic styling
			root.render(
				<User
					className="h-4 w-4"
					style={{
						fill: this._showingMyCheckins ? 'currentColor' : 'none',
						transition: 'fill 0.2s ease',
					}}
				/>,
			)

			// Update button styles (same as FavoritesControl)
			Object.assign(button.style, {
				padding: '6px',
				backgroundColor: '#fff',
				border: 'none',
				borderRadius: '3px',
				cursor: 'pointer',
				color: '#000',
				boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
				width: '30px',
				height: '30px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			})

			// Add hover effect
			button.onmouseover = () => {
				button.style.backgroundColor = '#f2f2f2'
			}
			button.onmouseout = () => {
				button.style.backgroundColor = '#fff'
			}

			button.onclick = () => {
				this._showingMyCheckins = !this._showingMyCheckins

				// Update the User icon fill
				root.render(
					<User
						className="h-4 w-4"
						style={{
							fill: this._showingMyCheckins ? 'currentColor' : 'none',
							transition: 'fill 0.2s ease',
						}}
					/>,
				)

				// Update URL query parameter
				const searchParams = new URLSearchParams(window.location.search)
				if (this._showingMyCheckins) {
					searchParams.set('meus_checkins', 'true')
				} else {
					searchParams.delete('meus_checkins')
				}
				window.history.pushState(
					{},
					'',
					`${window.location.pathname}?${searchParams.toString()}`,
				)

				// Clear the loaded stations cache when filter changes
				setLoadedStations(new Map()) // Reset cache

				// Trigger a new fetch by fetching stations for the current bounds
				const bounds = this._map.getBounds()
				const nlat = bounds._ne.lat.toString()
				const nlng = bounds._ne.lng.toString()
				const slat = bounds._sw.lat.toString()
				const slng = bounds._sw.lng.toString()
				const powerFilter = searchParams.get('power_filter') // Keep power filter
				const myCheckinsFilter = searchParams.get('meus_checkins') // Get updated checkins filter

				let urlToLoad = `/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}`
				if (powerFilter) {
					urlToLoad += `&power_filter=${powerFilter}`
				}
				if (myCheckinsFilter) {
					urlToLoad += `&meus_checkins=${myCheckinsFilter}`
				}

				fetcher.load(urlToLoad)
			}

			this._container.appendChild(button)
			return this._container
		}

		onRemove() {
			this._container.parentNode.removeChild(this._container)
			this._map = undefined
		}
	}

	const mapContainer = useRef(null)
	const map = useRef(null)
	const [lng] = useState(decode(userLon))
	const [lat] = useState(decode(userLat))
	const [zoom] = useState(zoomLevel)
	const fetcher = useFetcher()
	const routeFetcher = useFetcher()
	const navigate = useNavigate()
	const [filter, setFilter] = useState(0)
	const [blockedStations, setBlockedStations] = useState([])
	const [searchParams, setSearchParams] = useSearchParams()
	const [routeModalOpen, setRouteModalOpen] = useState(
		searchParams.get('planejar') === 'viagem',
	)
	const departureFetcher = useFetcher()
	const arrivalFetcher = useFetcher()
	const directionsRef = useRef(null)
	const mapRef = useRef(null)
	const sheetScrollRef = useRef(null)
	const interactionsSetupRef = useRef(false) // Ref to track if interactions are set up

	//Departure
	const [isDepartureDisabled, setDepartureDisabled] = useState(false)
	const [departureValue, setDepartureValue] = useState()
	const [departureQuery, setDepartureQuery] = useState()
	const [departureOptions, setDepartureOptions] = useState([])
	const [arrivalOptions, setArrivalOptions] = useState([])

	//Arrival
	const [isArrivalDisabled, setArrivalDisabled] = useState(false)
	const [arrivalValue, setArrivalValue] = useState()
	const [arrivalQuery, setArrivalQuery] = useState()

	const [range, setRange] = useState([300])
	const [batteryCapacity, setBatteryCapacity] = useState([60])
	const [initialBattery, setInitialBattery] = useState([100])
	const [arrivalBatteryTarget, setArrivalBatteryTarget] = useState([15])
	const [routeFeatures, setRouteFeatures] = useState([])
	const [routeStations, setRouteStations] = useState([])
	const [routeTotalTime, setRouteTotalTime] = useState(0)
	const [routeId, setRouteId] = useState(null)
	const [routeTotalDistance, setRouteTotalDistance] = useState(0)
	const [routeTotalChargingTime, setRouteTotalChargingTime] = useState(0)
	const [routeTotalDrivingTime, setRouteTotalDrivingTime] = useState(0)
	const [error, setError] = useState(null)
	const [isRouting, setIsRouting] = useState(false)
	const [selectedCar, setSelectedCar] = useState(() => {
		if (userCars.length > 0) {
			const mainCar = userCars.find((car) => car.isMain)
			return mainCar || userCars[0]
		}
		return null
	})
	const [tab, setTab] = useState('carro')

	const [loadedStations, setLoadedStations] = useState(new Map()) // <-- State for loaded stations
	const maxStations = 500 // Maximum stations to keep in memory // <-- Max stations constant

	const [routeProgress, setRouteProgress] = useState(0)
	const [routeProgressText, setRouteProgressText] = useState(
		'Iniciando cálculo da rota...',
	)
	const [wasRouting, setWasRouting] = useState(false) // Track if routing has occurred
	const [routeJustReset, setRouteJustReset] = useState(false) // Flag for reset
	const [iconsLoaded, setIconsLoaded] = useState(false) // State to track icon loading

	// State for fade transitions
	const [isProgressMounted, setIsProgressMounted] = useState(false)
	const [isProgressVisible, setIsProgressVisible] = useState(false)
	const [isButtonBlockMounted, setIsButtonBlockMounted] = useState(true) // Start visible
	const [isButtonBlockVisible, setIsButtonBlockVisible] = useState(true) // Start visible

	let stationPopup = null // Added for tooltip popup instance
	let hoveredStationId = null // Track the currently hovered station ID
	const routeFeaturesLengthRef = useRef(routeFeatures.length) // Ref to track length

	const calculateScore = (positives, negatives) => {
		const total = (positives || 0) + (negatives || 0)
		if (total === 0) {
			return null // Return null if no ratings
		}
		// Simple weighted score: 1 + (positive ratio * 4)
		const score = 1 + ((positives || 0) / total) * 4
		return parseFloat(score.toFixed(1)) // Return score rounded to one decimal place
	}

	// <-- Helper function to check if station is in bounds
	const isStationInBounds = React.useCallback((station, bounds) => {
		const lat = decode(station.latitude)
		const lng = decode(station.longitude)
		return (
			lng >= bounds._sw.lng &&
			lng <= bounds._ne.lng &&
			lat >= bounds._sw.lat &&
			lat <= bounds._ne.lat
		)
	}, []) // decode is stable, no need for dependency

	const RadioFilter = ({ onChange, currentPowerFilter }) => {
		const [isMinimized, setIsMinimized] = useState(!currentPowerFilter)
		const [selectedFilter, setSelectedFilter] = useState(
			currentPowerFilter || '',
		)

		// Update local state when currentPowerFilter changes from URL
		useEffect(() => {
			setSelectedFilter(currentPowerFilter || '')
		}, [currentPowerFilter])

		// Update global filter state when selectedFilter changes
		useEffect(() => {
			if (selectedFilter) {
				setFilter(selectedFilter)
			} else {
				setFilter(0) // Default to "Todas"
			}
		}, [selectedFilter])

		const handleFilterChange = (e) => {
			setSelectedFilter(e.target.value)
			onChange(e)
		}

		return (
			<div className="power-filter-container">
				<div
					className="power-filter-header"
					onClick={() => setIsMinimized(!isMinimized)}
				>
					<Zap className="power-filter-icon-left" />
					<span className="power-filter-title">Potência Média</span>
					{isMinimized ? (
						<ChevronDown className="power-filter-icon-right" />
					) : (
						<ChevronUp className="power-filter-icon-right" />
					)}
				</div>
				{!isMinimized && (
					<form className="power-filter-form">
						<div className="power-filter-option">
							<input
								type="radio"
								id="todos"
								name="power_filter"
								value=""
								checked={selectedFilter !== '20' && selectedFilter !== '50'}
								onChange={handleFilterChange}
								className="power-filter-radio"
							/>
							<label className="power-filter-label" htmlFor="todos">
								Todas
							</label>
						</div>
						<div className="power-filter-option">
							<input
								type="radio"
								id="20kw"
								name="power_filter"
								value="20"
								checked={selectedFilter === '20'}
								onChange={handleFilterChange}
								className="power-filter-radio"
							/>
							<label className="power-filter-label" htmlFor="20kw">
								&gt; 20 kW
							</label>
						</div>
						<div className="power-filter-option">
							<input
								type="radio"
								id="50kw"
								name="power_filter"
								value="50"
								checked={selectedFilter === '50'}
								onChange={handleFilterChange}
								className="power-filter-radio"
							/>
							<label className="power-filter-label" htmlFor="50kw">
								&gt; 50 kW
							</label>
						</div>
					</form>
				)}
			</div>
		)
	}

	function debounce(func, wait) {
		let timeout
		let lastQuery = ''
		let lastCallTime = 0

		return function executedFunction(...args) {
			const query = args[0]
			const now = Date.now()

			// Always clear the existing timeout
			clearTimeout(timeout)

			// Don't search if query is empty
			if (!query || query.trim().length === 0) {
				return
			}

			// If typing fast (less than wait/2 ms since last call), reduce the wait time
			const timeSinceLastCall = now - lastCallTime
			const adjustedWait = timeSinceLastCall < wait / 2 ? wait / 2 : wait

			const later = () => {
				lastQuery = query
				lastCallTime = now
				func(...args)
			}

			timeout = setTimeout(later, adjustedWait)
		}
	}

	const debouncedDepartureSearch = React.useCallback(
		debounce((query) => {
			if (query && query.trim()) {
				departureFetcher.load(`/resources/finder?q=${query}`)
			}
		}, 100),
		[],
	)

	useEffect(() => {
		if (departureFetcher.state === 'loading') {
			setDepartureOptions([]) // Clear old results while loading
		} else if (departureFetcher.data) {
			setDepartureOptions(departureFetcher.data)
		}
	}, [departureFetcher])

	const debouncedArrivalSearch = React.useCallback(
		debounce((query) => {
			if (query && query.trim()) {
				arrivalFetcher.load(`/resources/finder?q=${query}`)
			}
		}, 100),
		[],
	)

	useEffect(() => {
		if (arrivalFetcher.state === 'loading') {
			setArrivalOptions([]) // Clear old results while loading
		} else if (arrivalFetcher.data) {
			setArrivalOptions(arrivalFetcher.data)
		}
	}, [arrivalFetcher])

	useEffect(() => {
		if (blockedStations.length > 0) {
			handleCreateRoute()
		}
	}, [blockedStations])

	const iconTypeToColor = {
		AR: 'vermelho',
		BR: 'vermelho',
		YR: 'vermelho',
		GR: 'vermelho',
		A: 'amarelo',
		B: 'amarelo',
		H: 'amarelo',
		Y: 'azul',
		G: 'verde',
		MINIAR: 'minis-vermelho',
		MINIBR: 'minis-vermelho',
		MINIYR: 'minis-vermelho',
		MINIGR: 'minis-vermelho',
		MINIA: 'minis-amarelo',
		MINIB: 'minis-amarelo',
		MINIH: 'minis-amarelo',
		MINIY: 'minis-azul',
		MINIG: 'minis-verde',
	}

	const openLink = (url, id) => {
		const searchParams = new URLSearchParams(window.location.search)

		navigate(`/mapa/${url}${id}?${searchParams.toString()}`, {
			preventScrollReset: true,
			state: { history: `/mapa?${searchParams.toString()}` },
		})
	}

	// Effect watching routeFetcher state and data
	useEffect(() => {
		// Only process route data if the fetcher is idle, has data, and map exists.
		if (routeFetcher.state === 'idle' && routeFetcher.data && mapRef.current) {
			if (routeFetcher.data.error) {
				setError(routeFetcher.data.error)
				// Removed setIsRouting(false) here - let UI handle based on fetcher.state
				return
			}

			const route = routeFetcher.data

			const waypoints = route.route_details.map((point) => {
				if (point.point === 'Station') {
					return point.station.coords
				} else {
					return point.coords
				}
			})

			// Extract station details from route_details
			const stationDetails = route.route_details
				.filter((point) => point.point === 'Station')
				.map((station) => ({
					id: station.station.id,
					url: station.station.url,
					name: station.station.name, // Add name
					icon_type: station.station.icon_type,
					latitude: station.station.latitude,
					longitude: station.station.longitude,
					averagePower: station.station.averagePower, // Add averagePower
					positives: station.station.positives, // Add positives
					negatives: station.station.negatives, // Add negatives
					city: station.station.city, // Add city
					state: station.station.state, // Add state
				}))

			// Ensure waypoints are in [longitude, latitude] format
			const formattedWaypoints = waypoints.map((coord) =>
				Array.isArray(coord) ? [coord[1], coord[0]] : coord,
			)

			if (directionsRef.current) {
				// Check if directionsRef is initialized
				directionsRef.current.setWaypoints(formattedWaypoints)
			}

			// Calculate the bounding box of the route
			const bounds = new maplibregl.LngLatBounds()
			formattedWaypoints.forEach((point) => bounds.extend(point))

			let routeFeaturesData = stationDetails.map((station) => ({
				// Renamed variable
				type: 'Feature',
				properties: {
					id: station.id,
					url: station.url,
					name: station.name, // Add name to feature properties
					averagePower: station.averagePower,
					positives: station.positives,
					negatives: station.negatives,
					city: station.city,
					state: station.state,
					icon: favorites.includes(station.id)
						? 'roxo'
						: iconTypeToColor[station.icon_type],
				},
				geometry: {
					type: 'Point',
					coordinates: [station.longitude, station.latitude],
				},
			}))

			routeFeaturesData.unshift({
				type: 'Feature',
				properties: {
					id: 'start',
					icon: 'start',
				},
				geometry: {
					type: 'Point',
					coordinates: [formattedWaypoints[0][0], formattedWaypoints[0][1]],
				},
			})

			routeFeaturesData.push({
				type: 'Feature',
				properties: {
					id: 'finish',
					icon: 'finish',
				},
				geometry: {
					type: 'Point',
					coordinates: [
						formattedWaypoints[formattedWaypoints.length - 1][0],
						formattedWaypoints[formattedWaypoints.length - 1][1],
					],
				},
			})

			setRouteFeatures(routeFeaturesData) // Update state with new data
			setRouteStations(route.route_details)
			setRouteTotalTime(route.total_time)
			setRouteTotalDistance(route.total_distance)
			setRouteTotalChargingTime(route.total_charging_time)
			setRouteTotalDrivingTime(route.total_driving_time)
			setRouteId(route.routeId)

			// --- Fit map and trigger fetch ---
			mapRef.current.fitBounds(bounds, {
				padding: 50, // Add some padding around the bounds
				maxZoom: 15, // Limit the max zoom level
				pitch: 40,
			})

			// Listen for the *next* moveend event after fitBounds starts
			mapRef.current.once('moveend', () => {
				const searchParams = new URLSearchParams(window.location.search)
				// Ensure bounds are fetched *after* moveend
				const finalBounds = mapRef.current.getBounds()
				const nlat = finalBounds._ne.lat.toString()
				const nlng = finalBounds._ne.lng.toString()
				const slat = finalBounds._sw.lat.toString()
				const slng = finalBounds._sw.lng.toString()

				// Get current power_filter query string
				const powerFilter = searchParams.get('power_filter')
				const myCheckinsFilter = searchParams.get('meus_checkins') // Get reviews filter

				// Construct the URL for the fetcher
				let urlToLoad = `/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}`
				if (powerFilter) {
					urlToLoad += `&power_filter=${powerFilter}`
				}
				if (myCheckinsFilter) {
					urlToLoad += `&meus_checkins=${myCheckinsFilter}` // Add reviews filter
				}

				// Load stations for the final view without clearing the cache
				fetcher.load(urlToLoad)
			})
			// --- End fit map and trigger fetch ---
		} else if (routeFetcher.state === 'idle' && !routeFetcher.data) {
			// Handle cases where idle but no data (e.g., initial load, cleared route)
			// Removed setIsRouting(false) here
		}
	}, [routeFetcher.state, routeFetcher.data]) // Dependencies: only state and data of the route fetcher

	useEffect(() => {
		if (map.current) return // stops map from intializing more than once

		map.current = new maplibregl.Map({
			container: mapContainer.current,
			// style: 'https://tiles.openfreemap.org/styles/liberty',
			style: mapData, // Use the modified style
			center: [lng, lat],
			zoom: zoom,
			attributionControl: false,
			fadeDuration: 100,
		})

		map.current.on('load', async () => {
			mapRef.current = map.current // Set mapRef early

			// Add sources first
			map.current.addSource('routeStations', {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: [], // Start with empty features
				},
			})

			map.current.addSource('stations', {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: [], // Start with empty features
				},
			})

			// Initialize directions control
			const directions = new DistanceMeasurementMapLibreGlDirections(
				map.current,
				config,
			)
			directionsRef.current = directions // Set directionsRef

			directionsRef.current.on('fetchroutesstart', () => {
				setIsRouting(true)
			})

			directionsRef.current.on('fetchroutesend', () => {
				setIsRouting(false)
			})

			// Add other controls (geocoder, navigation, etc.)
			const geocoderApi = {
				forwardGeocode: async (config) => {
					const features = []
					try {
						const request = `/resources/city-station-finder?q=${config.query}`
						const response = await fetch(request)
						const geojson = await response.json()

						for (const feature of geojson.features) {
							const city = feature.properties.name
							const state = feature.properties.context.region.name
							const country = feature.properties.context.country.name

							const point = {
								type: 'Feature',
								geometry: feature.geometry,
								place_name: `${city}, ${state}, ${country}`,
								properties: feature.properties,
								place_type: ['place'],
								center: feature.geometry.coordinates,
								bbox: feature.properties.bbox,
							}
							features.push(point)
						}
					} catch (e) {
						console.error(`Failed to forwardGeocode with error: ${e}`)
					}
					return {
						features,
					}
				},
			}

			const routeControl = new RouteControl()
			map.current.addControl(routeControl, 'top-right')
			map.current.addControl(new FavoritesControl(), 'top-right')
			map.current.addControl(new MyReviewsControl(), 'top-right')
			map.current.addControl(
				new maplibregl.GeolocateControl({
					positionOptions: {
						enableHighAccuracy: true,
					},
					trackUserLocation: true,
					showAccuracyCircle: false,
				}),
			)
			map.current.addControl(
				new maplibregl.NavigationControl({
					showZoom: true,
					showCompass: false,
					visualizePitch: false,
				}),
				'top-right',
			)
			map.current.addControl(
				new CustomMaplibreGeocoder(geocoderApi, {
					maplibregl,
					showResultsWhileTyping: true,
					debounceSearch: 200,
					showResultMarkers: false,
					marker: false,
					popup: false,
					placeholder: 'Cidade, Estação...',
					flyTo: {
						padding: 50, // Add padding around the bounds
					},
					clearOnBlur: true,
					language: 'pt-BR',
					fitBounds: true,
					bbox: true,
				}),
				'top-left',
			)
			map.current.addControl(new RadioFilterControl(), 'top-left')

			// Load icons
			const colors = [
				'verde',
				'roxo',
				'amarelo',
				'azul',
				'vermelho',
				'start',
				'finish',
				'minis-verde',
				'minis-roxo',
				'minis-amarelo',
				'minis-azul',
				'minis-vermelho',
			]

			Promise.all(
				colors.map(async (color) => {
					try {
						const image = await map.current.loadImage(`/${color}.png`)
						if (!map.current.hasImage(color)) {
							map.current.addImage(color, image.data)
						}
					} catch (error) {
						console.error(`Failed to load image for ${color}:`, error)
					}
				}),
			).then(() => {
				// Set iconsLoaded state after a brief delay to allow processing
				setTimeout(() => {
					if (map.current) {
						// Check if map still exists
						setIconsLoaded(true)
						// Trigger initial fetch after icons are likely ready
						fetchStationsForBounds()
					}
				}, 0) // Use setTimeout 0 to yield execution briefly
			})

			// Define the fetch function inside the useEffect or make it accessible
			const fetchStationsForBounds = () => {
				const searchParams = new URLSearchParams(window.location.search)
				const bounds = map.current.getBounds()
				const nlat = bounds._ne.lat.toString()
				const nlng = bounds._ne.lng.toString()
				const slat = bounds._sw.lat.toString()
				const slng = bounds._sw.lng.toString()

				// get current query strings
				const powerFilter = searchParams.get('power_filter')
				const myCheckinsFilter = searchParams.get('meus_checkins') // Get reviews filter

				// Construct the URL for the fetcher
				let urlToLoad = `/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}`
				if (powerFilter) {
					urlToLoad += `&power_filter=${powerFilter}`
				}
				if (myCheckinsFilter) {
					urlToLoad += `&meus_checkins=${myCheckinsFilter}` // Add reviews filter
				}

				// Load stations without clearing the cache
				fetcher.load(urlToLoad)
			}

			// Use the function for both moveend and zoomend
			map.current.on('moveend', fetchStationsForBounds)
			map.current.on('zoomend', fetchStationsForBounds) // <-- Add zoomend listener

			// Initial load call moved to the .then() block after icon loading
		})
	}, [lng, lat, zoom]) // Removed routeStations dependency here as layers/interactions depend on iconsLoaded

	// useEffect to add layers and interactions once icons are loaded
	useEffect(() => {
		if (iconsLoaded && map.current && !interactionsSetupRef.current) {
			// --- Layer Definitions (Bottom to Top) ---
			const layersToAdd = [
				{
					id: 'stations-vermelho',
					source: 'stations',
					filterIcons: ['vermelho', 'minis-vermelho'],
					size: 0.7,
				},
				{
					id: 'stations-amarelo',
					source: 'stations',
					filterIcons: ['amarelo', 'minis-amarelo'],
					size: 0.7,
				},
				{
					id: 'stations-verde',
					source: 'stations',
					filterIcons: ['verde', 'minis-verde'],
					size: 0.7,
				},
				{
					id: 'stations-azul',
					source: 'stations',
					filterIcons: ['azul', 'minis-azul'],
					size: 0.7,
				},
				{
					id: 'stations-roxo',
					source: 'stations',
					filterIcons: ['roxo', 'minis-roxo'],
					size: 0.7,
				},
				{
					id: 'routeStations-vermelho',
					source: 'routeStations',
					filterIcons: ['vermelho', 'minis-vermelho'],
					size: 1,
				},
				{
					id: 'routeStations-amarelo',
					source: 'routeStations',
					filterIcons: ['amarelo', 'minis-amarelo'],
					size: 1,
				},
				{
					id: 'routeStations-verde',
					source: 'routeStations',
					filterIcons: ['verde', 'minis-verde'],
					size: 1,
				},
				{
					id: 'routeStations-azul',
					source: 'routeStations',
					filterIcons: ['azul', 'minis-azul'],
					size: 1,
				},
				{
					id: 'routeStations-roxo',
					source: 'routeStations',
					filterIcons: ['roxo', 'minis-roxo'],
					size: 1,
				},
				{
					id: 'routeStations-start-finish',
					source: 'routeStations',
					filterIcons: ['start', 'finish'],
					size: 1,
				},
			]

			layersToAdd.forEach((layerInfo) => {
				if (!map.current.getLayer(layerInfo.id)) {
					map.current.addLayer({
						id: layerInfo.id,
						type: 'symbol',
						source: layerInfo.source,
						layout: {
							'icon-image': '{icon}',
							'icon-size': layerInfo.size,
							'icon-allow-overlap': true,
							'text-allow-overlap': true,
							'icon-ignore-placement': true,
							'text-ignore-placement': true,
							'icon-anchor': 'bottom',
						},
						filter: ['in', ['get', 'icon'], ['literal', layerInfo.filterIcons]],
					})
				}
			})

			// Define all clickable layers
			const clickableLayers = layersToAdd.map((l) => l.id)

			// Add click listeners for all layers
			clickableLayers.forEach((layerId) => {
				// Check if the layer exists before adding a listener
				if (map.current.getLayer(layerId)) {
					map.current.off('click', layerId) // Remove previous listener if any
					map.current.on('click', layerId, (e) => {
						if (e.features.length > 0) {
							const { id, url } = e.features[0].properties
							// Prevent navigation for start/finish markers
							if (id === 'start' || id === 'finish') return
							openLink(url, id)
						}
					})
				}
			})

			// --- Tooltip Logic Start (Refactored) ---
			const stationLayers = clickableLayers // Use the same list for tooltips

			map.current.off('mousemove') // Remove previous listener if any
			map.current.on('mousemove', (e) => {
				// --- Mobile device check ---
				if (isMobileDevice) {
					// On mobile, ensure cursor is default and no tooltip is shown
					map.current.getCanvas().style.cursor = '' // Reset cursor just in case
					if (stationPopup) {
						// Remove any lingering popup
						stationPopup.remove()
						stationPopup = null
						hoveredStationId = null
					}
					return // Don't proceed with hover/tooltip logic on mobile
				}
				// --- End of mobile check ---

				// Define a pixel buffer around the cursor
				const pixelBuffer = 3 // Reduced buffer size back to 3
				const queryBox = [
					[e.point.x - pixelBuffer, e.point.y - pixelBuffer],
					[e.point.x + pixelBuffer, e.point.y + pixelBuffer],
				]

				// Query features within the bounding box around the cursor
				const features = map.current.queryRenderedFeatures(queryBox, {
					layers: stationLayers,
				})

				// --- Step 1: Always determine if a station is hovered and set cursor ---
				let isHoveringStation = features.length > 0
				let feature = isHoveringStation ? features[0] : null

				map.current.getCanvas().style.cursor = isHoveringStation
					? 'pointer'
					: ''

				// --- Step 2: Handle Tooltip Logic ---
				if (isHoveringStation) {
					const stationId = feature.properties.id // ensure semicolon
					const stationName = feature.properties.name // ensure semicolon

					// Only update if hovering a *new* station
					if (stationId !== hoveredStationId) {
						hoveredStationId = stationId

						// Helper function to format score (optional, adjust as needed)
						const formatScore = (score) => {
							if (score === null || score === undefined) return 'N/A'
							return score.toFixed(1)
						}
						const formatPower = (power) => {
							if (power === null || power === undefined || power === 0)
								return 'N/A'
							return `${power.toFixed(0)} kW`
						}
						const formatRatings = (positives, negatives) => {
							const total = (positives || 0) + (negatives || 0)
							if (total === 0) return 'N/A'
							return total.toString()
						}
						// Calculate score using the helper function
						const calculatedScore = calculateScore(
							feature.properties.positives,
							feature.properties.negatives,
						)

						// Ensure coordinates are numbers (handle longitude wrapping)
						const coordinates = [...feature.geometry.coordinates] // Clone to avoid modifying original
						while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
							coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
						}

						// Define standard and mini offset bases first
						const standardOffsets = {
							top: [0, 0],
							'top-left': [10, 10],
							'top-right': [-10, 10],
							'bottom-left': [10, -10],
							'bottom-right': [-10, -10],
							left: [10, -10],
							right: [-10, -10],
						}
						const miniOffsets = {
							top: [0, 0],
							'top-left': [5, 5],
							'top-right': [-5, 5],
							'bottom-left': [5, -5],
							'bottom-right': [-5, -5],
							left: [5, 0],
							right: [-5, 0],
						}

						// Determine tooltip offset based on icon type and route status
						const isRouteActive = routeFeaturesLengthRef.current > 0
						const sourceLayer = feature.layer.source // 'stations' or 'routeStations'
						let tooltipOffset = {}

						if (stationId === 'start' || stationId === 'finish') {
							// Double offset for Start/Finish icons (standard base for sides/corners)
							tooltipOffset = {
								...standardOffsets,
								top: [0, 0],
								bottom: [0, -50],
							}
						} else if (isRouteActive) {
							if (sourceLayer === 'routeStations') {
								// Double offset for intermediate route stations (standard base for sides/corners)
								tooltipOffset = {
									...standardOffsets,
									top: [0, 0],
									bottom: [0, -50],
								}
							} else {
								// sourceLayer === 'stations'
								// Half offset for background stations (mini base for sides/corners)
								tooltipOffset = {
									...miniOffsets,
									top: [0, 0],
									bottom: [0, -5],
								}
							}
						} else {
							// Standard offset when no route is active (standard base for sides/corners)
							tooltipOffset = {
								...standardOffsets,
								top: [0, 0],
								bottom: [0, -30],
							}
						}

						// Create or update popup
						if (!stationPopup) {
							stationPopup = new maplibregl.Popup({
								closeButton: false,
								closeOnClick: false,
								offset: tooltipOffset, // Use the determined offset
								className: 'station-tooltip',
							})
						} else {
							// Update existing popup's offset
							stationPopup.options.offset = tooltipOffset
						}

						const avgPower = formatPower(feature.properties.averagePower)
						// Use the calculated score for display
						const scoreDisplay =
							calculatedScore !== null ? formatScore(calculatedScore) : 'N/A'
						const city = feature.properties.city
						const state = feature.properties.state
						const location =
							city && state
								? `${formatText(city)}, ${formatState(state)}`.replace('()', '')
								: city || state || 'N/A'
						const positives = feature.properties.positives || 0
						const negatives = feature.properties.negatives || 0
						const ratingsCount = formatRatings(positives, negatives)

						let tooltipHTML = ''

						if (stationId === 'start' || stationId === 'finish') {
							const title = stationId === 'start' ? 'Partida' : 'Chegada'
							const [lon, lat] = coordinates
							tooltipHTML = `
								<div class="station-tooltip-content">
									<div class="station-tooltip-name">${title}</div>
									<div class="station-tooltip-details">
										<span>Lat: ${lat.toFixed(4)}, Lon: ${lon.toFixed(4)}</span>
									</div>
								</div>
							`
						} else {
							tooltipHTML = `
								<div class="station-tooltip-content">
									<div class="station-tooltip-name">${stationName}</div>
									<div class="station-tooltip-details">
										${avgPower !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-zap tooltip-icon"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg><span>${avgPower}</span></div>` : ''}
										${scoreDisplay !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-star tooltip-icon"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg><span>${scoreDisplay}</span></div>` : ''}
										${ratingsCount !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide tooltip-icon"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg><span>${ratingsCount} ${positives + negatives === 1 ? 'avaliação' : 'avaliações'}</span></div>` : ''}
										${location !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin tooltip-icon"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg><span>${location}</span></div>` : ''}
									</div>
								</div>
							`
						}

						stationPopup
							.setLngLat(coordinates) // Use potentially adjusted coordinates
							.setHTML(tooltipHTML)
							.addTo(map.current)
					}
				} else {
					// No station hovered, remove popup if it exists
					if (hoveredStationId) {
						if (stationPopup) stationPopup.remove()
						stationPopup = null
						hoveredStationId = null
					}
				}
			}) // End mousemove handler

			// Removed individual layer listeners
			map.current.off('mouseleave', stationLayers.join(',')) // Remove previous listener if any
			map.current.on('mouseleave', stationLayers.join(','), () => {
				if (!isMobileDevice) {
					map.current.getCanvas().style.cursor = ''
					if (stationPopup) {
						stationPopup.remove()
						stationPopup = null
						hoveredStationId = null
					}
				}
			})

			interactionsSetupRef.current = true // Mark interactions as set up
		}
	}, [iconsLoaded]) // Depend on iconsLoaded state

	useEffect(() => {
		if (fetcher.data && map.current) {
			const fetchedStationsMap = new Map()
			const searchParams = new URLSearchParams(window.location.search)
			const powerFilter = searchParams.get('power_filter')
			const myCheckinsFilter = searchParams.get('meus_checkins') === 'true'
			const powerFilterValue = powerFilter ? parseInt(powerFilter) : 0

			// Filter stations based on power filter before adding to map
			fetcher.data.stations.forEach((station) => {
				// Only add stations that meet the power filter requirement
				// For myCheckinsFilter, we trust the backend filtering
				if (!powerFilterValue || station.averagePower >= powerFilterValue) {
					fetchedStationsMap.set(station.id, station)
				}
			})

			// Merge with existing stations, applying both filters to cached stations
			let preliminaryCombinedStations = new Map()

			// Add filtered fetched stations (these already passed both filters from backend)
			for (const [id, station] of fetchedStationsMap) {
				preliminaryCombinedStations.set(id, station)
			}

			// Add filtered cached stations that aren't in the new fetch
			// For cached stations, we need to:
			// 1. Check they meet the power filter requirement
			// 2. If myCheckinsFilter is true, only keep stations that are in the current fetch
			//    (since those are the only ones we know have reviews)
			for (const [id, station] of loadedStations) {
				if (
					!fetchedStationsMap.has(id) && // Not in new fetch
					(!powerFilterValue || station.averagePower >= powerFilterValue) && // Meets power filter
					!myCheckinsFilter // Only include if we're not filtering by reviews
				) {
					preliminaryCombinedStations.set(id, station)
				}
			}

			let finalCombinedStations = preliminaryCombinedStations

			// Apply eviction strategy if exceeding the maximum limit
			if (preliminaryCombinedStations.size > maxStations) {
				const bounds = map.current.getBounds()
				const stationsArray = Array.from(preliminaryCombinedStations.entries())

				stationsArray.sort(([, a], [, b]) => {
					const aInBounds = isStationInBounds(a, bounds)
					const bInBounds = isStationInBounds(b, bounds)
					if (aInBounds && !bInBounds) return -1
					if (!aInBounds && bInBounds) return 1
					const aIsFromFetch = fetchedStationsMap.has(a.id)
					const bIsFromFetch = fetchedStationsMap.has(b.id)
					if (aIsFromFetch && !bIsFromFetch) return -1
					if (!aIsFromFetch && bIsFromFetch) return 1
					return 0
				})

				finalCombinedStations = new Map(stationsArray.slice(0, maxStations))
			}

			// Compare before updating state
			let needsStateUpdate = false
			if (finalCombinedStations.size !== loadedStations.size) {
				needsStateUpdate = true
			} else {
				for (const [key] of finalCombinedStations) {
					if (!loadedStations.has(key)) {
						needsStateUpdate = true
						break
					}
				}
			}

			if (needsStateUpdate) {
				setLoadedStations(finalCombinedStations)
			}

			// Update map source with filtered stations
			const isRouteCurrentlyActive = routeFeatures && routeFeatures.length > 0
			const features = Array.from(finalCombinedStations.values()).map(
				(station) => {
					const isFavorite = favorites.includes(station.id)
					let iconName = ''

					if (isFavorite) {
						iconName = isRouteCurrentlyActive ? 'minis-roxo' : 'roxo'
					} else {
						const baseColor = iconTypeToColor[station.icon_type] || 'vermelho' // Default color if type unknown
						// Ensure MINI type exists, otherwise fallback to minis-<baseColor>
						const miniIconKey = `MINI${station.icon_type}`
						iconName = isRouteCurrentlyActive
							? iconTypeToColor[miniIconKey] || `minis-${baseColor}`
							: baseColor
					}

					return {
						type: 'Feature',
						properties: {
							id: station.id,
							url: station.url,
							name: station.name,
							averagePower: station.averagePower,
							positives: station.positives,
							negatives: station.negatives,
							city: station.city,
							state: station.state,
							icon_type: station.icon_type, // Keep original type for reference if needed
							icon: iconName, // Use calculated icon name
						},
						geometry: {
							type: 'Point',
							coordinates: [
								decode(station.longitude),
								decode(station.latitude),
							],
						},
					}
				},
			)

			const filteredFeatures = features.filter(
				(feature) =>
					!routeFeatures?.some(
						(rf) => rf.properties.id === feature.properties.id,
					),
			)

			const source = map.current.getSource('stations')
			if (source) {
				source.setData({
					type: 'FeatureCollection',
					features: filteredFeatures,
				})
			}

			// Handle route-specific updates
			const routeSource = map.current.getSource('routeStations')
			if (isRouteCurrentlyActive) {
				const backgroundLayers = [
					'stations-vermelho',
					'stations-amarelo',
					'stations-verde',
					'stations-azul',
					'stations-roxo',
				]
				backgroundLayers.forEach((layerId) => {
					if (map.current.getLayer(layerId)) {
						map.current.setLayoutProperty(layerId, 'icon-size', 1.1) // Smaller size for background icons when route active
						map.current.setLayoutProperty(layerId, 'icon-anchor', 'bottom')
					}
				})

				const routeLayers = [
					'routeStations-vermelho',
					'routeStations-amarelo',
					'routeStations-verde',
					'routeStations-azul',
					'routeStations-roxo',
					'routeStations-start-finish',
				]
				routeLayers.forEach((layerId) => {
					if (map.current.getLayer(layerId)) {
						map.current.setLayoutProperty(layerId, 'icon-size', 1)
						map.current.setLayoutProperty(layerId, 'icon-anchor', 'bottom')
					}
				})

				if (routeSource) {
					routeSource.setData({
						type: 'FeatureCollection',
						features: routeFeatures, // Update route source with current route features
					})
				}
			} else {
				const allStationLayers = [
					'stations-vermelho',
					'stations-amarelo',
					'stations-verde',
					'stations-azul',
					'stations-roxo',
				]
				allStationLayers.forEach((layerId) => {
					if (map.current.getLayer(layerId)) {
						map.current.setLayoutProperty(layerId, 'icon-size', 0.7)
						map.current.setLayoutProperty(layerId, 'icon-anchor', 'bottom')
					}
				})

				// Ensure route source is cleared when route becomes inactive
				if (routeSource) {
					routeSource.setData({
						type: 'FeatureCollection',
						features: [],
					})
				}
			}

			// Trigger map repaint after source updates
			if (map.current) {
				map.current.triggerRepaint()
			}
		}
	}, [
		fetcher.data,
		loadedStations, // Need cache for merging
		routeFeatures,
		favorites,
		iconsLoaded, // Add iconsLoaded dependency
		// Removed isStationInBounds and iconTypeToColor assuming they are stable references defined outside the component or constant.
		// If they are defined inside the component and change, they should be added back.
	]) // Dependencies: viewport data, cache, route features, favorites, iconsLoaded.

	// Effect to resize map after initial mount to fix potential sizing issues
	useEffect(() => {
		if (map.current) {
			const resizeTimeout = setTimeout(() => {
				if (map.current) {
					map.current.resize()
					// Fire a moveend event to ensure all map features are properly loaded and positioned
					map.current.fire('moveend')
				}
			}, 150) // 150ms delay, adjust if needed

			return () => clearTimeout(resizeTimeout) // Cleanup timeout on unmount
		}
	}, []) // Empty dependency array ensures this runs only once after mount

	function handleCreateRoute(e) {
		// Clear previous errors first
		setError(null)

		// Check if departure or arrival is missing
		if (!departureValue || !arrivalValue) {
			setError('Por favor, preencha os locais de saída e chegada.')
			return // Stop execution if validation fails
		}

		// Proceed if validation passes
		const formData = new FormData()
		formData.append('start', `${departureValue.lat},${departureValue.lon}`)
		formData.append('end', `${arrivalValue.lat},${arrivalValue.lon}`)
		formData.append('range', range[0].toString())
		formData.append('battery_capacity', batteryCapacity.toString())
		formData.append('initial_battery', initialBattery.toString())
		formData.append('arrival_battery_target', arrivalBatteryTarget.toString())
		formData.append('car_id', selectedCar?.id?.toString())
		formData.append('tab', tab.toString())
		formData.append(
			'blocked_stations',
			blockedStations.map((station) => station.stationId).join(','),
		)

		routeFetcher.submit(formData, { method: 'post', action: '/mapa' })
	}

	React.useEffect(() => {
		if (departureQuery === undefined) return

		if (departureQuery && departureQuery.trim()) {
			debouncedDepartureSearch(departureQuery)
		} else {
			setDepartureOptions([])
		}
	}, [departureQuery, debouncedDepartureSearch])

	React.useEffect(() => {
		if (arrivalQuery === undefined) return

		if (arrivalQuery && arrivalQuery.trim()) {
			debouncedArrivalSearch(arrivalQuery)
		} else {
			setArrivalOptions([])
		}
	}, [arrivalQuery, debouncedArrivalSearch])

	useEffect(() => {
		const planejar = searchParams.get('planejar')
		if (planejar === 'viagem') {
			setRouteModalOpen(true)
		}
	}, [searchParams])

	useEffect(() => {
		if (!map.current) return

		// Find the route control button
		const routeButton = document.querySelector(
			'.maplibregl-ctrl-group button[aria-label*="Viagem"], .maplibregl-ctrl-group button[aria-label*="Rota"]',
		)

		if (routeButton) {
			// Update aria-label
			routeButton.setAttribute(
				'aria-label',
				routeStations.length > 0 ? 'Detalhes da Rota' : 'Planejar Viagem',
			)

			// Update text content
			const textSpan = routeButton.querySelector('span')
			if (textSpan) {
				textSpan.textContent =
					routeStations.length > 0 ? 'Detalhes da Rota' : 'Planejar Viagem'
			}
		}
	}, [routeStations])

	// Add this new effect to manage the progress bar
	useEffect(() => {
		// Only run this effect when routing starts or stops
		const currentlyRouting = routeFetcher.state === 'submitting' || isRouting

		if (currentlyRouting) {
			setWasRouting(true) // Mark that routing has started
			// Reset progress when starting a new route calculation
			setRouteProgress(0)
			setRouteProgressText('Iniciando cálculo da rota...')

			// Set up intervals to update progress
			// We'll create a pseudo-progress that completes in about 20 seconds
			// but slows down as it approaches 100%

			const progressInterval = setInterval(() => {
				setRouteProgress((prev) => {
					const newProgress = prev + (prev < 50 ? 2 : prev < 80 ? 1 : 0.5)

					// Update text based on progress
					if (newProgress >= 90) {
						setRouteProgressText('Finalizando o cálculo da rota...')
					} else if (newProgress >= 70) {
						setRouteProgressText('Calculando tempo de recarga...')
					} else if (newProgress >= 40) {
						setRouteProgressText('Calculando paradas necessárias...')
					} else if (newProgress >= 10) {
						setRouteProgressText('Analisando possíveis rotas...')
					}

					return Math.min(newProgress, 95) // Cap at 95% until complete
				})
			}, 200) // Update every 200ms

			return () => {
				clearInterval(progressInterval)
				// Cleanup interval, but don't set final progress here
			}
		} else if (routeFetcher.state === 'idle' && !isRouting) {
			// When routing is complete (idle state) AND it was previously routing
			if (wasRouting) {
				setRouteProgress(100)
				setRouteProgressText('Rota calculada com sucesso!')
				// Optional: reset wasRouting if the bar should disappear/reset fully
				// setWasRouting(false);
			}
			// If idle but never started routing (initial load), do nothing or ensure progress is 0
			// else {
			//  setRouteProgress(0); // Keep it at 0 initially
			// }
		}
	}, [routeFetcher.state, isRouting, wasRouting]) // Add wasRouting to dependencies

	// Effect for managing fade transitions between progress and button/error blocks
	useEffect(() => {
		const isLoading = routeFetcher.state !== 'idle' || isRouting
		let progressMountTimer
		let progressVisibleTimer
		let buttonMountTimer
		let buttonVisibleTimer

		if (isLoading) {
			// Show Progress Indicator
			setIsButtonBlockVisible(false) // Start fade-out button
			buttonMountTimer = setTimeout(() => setIsButtonBlockMounted(false), 300) // Unmount button after fade

			setIsProgressMounted(true) // Mount progress immediately
			// Use rAF or timeout 0 for better timing consistency across browsers
			progressVisibleTimer = setTimeout(() => setIsProgressVisible(true), 10) // Start fade-in progress shortly after mount
		} else {
			// Show Button/Error Block
			setIsProgressVisible(false) // Start fade-out progress
			progressMountTimer = setTimeout(() => setIsProgressMounted(false), 300) // Unmount progress after fade

			setIsButtonBlockMounted(true) // Mount button immediately
			buttonVisibleTimer = setTimeout(() => setIsButtonBlockVisible(true), 10) // Start fade-in button shortly after mount
		}

		return () => {
			clearTimeout(progressMountTimer)
			clearTimeout(progressVisibleTimer)
			clearTimeout(buttonMountTimer)
			clearTimeout(buttonVisibleTimer)
		}
	}, [routeFetcher.state, isRouting])

	// Scroll sheet content to top when routeStations becomes empty
	useEffect(() => {
		if (sheetScrollRef.current) {
			sheetScrollRef.current.scrollTop = 0
		}
	}, [routeStations])

	// Keep the ref updated when routeFeatures changes
	useEffect(() => {
		routeFeaturesLengthRef.current = routeFeatures.length
	}, [routeFeatures])

	const resetRoute = () => {
		// --- Existing Resets ---
		if (directionsRef.current) {
			directionsRef.current.clear()
			directionsRef.current.setWaypoints([]) // Explicitly clear waypoints
		}
		setRouteStations([])
		setRouteFeatures([])
		setRouteTotalTime(0)
		setRouteTotalDistance(0)
		setRouteTotalChargingTime(0)
		setRouteTotalDrivingTime(0)
		setRouteId(null)
		setBlockedStations([])
		setError(null) // Clear any previous errors

		// --- New Resets ---
		setDepartureValue(undefined)
		setArrivalValue(undefined)
		setDepartureQuery('') // Clear autocomplete input
		setArrivalQuery('') // Clear autocomplete input
		setDepartureDisabled(false) // Re-enable departure input if it was disabled by 'current location'
		setArrivalDisabled(false) // Re-enable arrival input (though it likely wasn't disabled)

		// Reset vehicle/manual settings to defaults
		setTab('carro') // Default to 'carro' tab
		if (userCars.length > 0) {
			const mainCar = userCars.find((car) => car.isMain)
			setSelectedCar(mainCar || userCars[0]) // Reset to main or first car
		} else {
			setSelectedCar(null)
		}
		// Reset manual sliders to default values
		setRange([300])
		setBatteryCapacity([60])
		setInitialBattery([100])
		setArrivalBatteryTarget([15])

		// Remove 'planejar=viagem' from URL
		const newParams = new URLSearchParams(searchParams)
		newParams.delete('planejar')
		setSearchParams(newParams, { replace: true }) // Use replace to avoid adding to history

		// --- Existing Map Resets ---
		if (mapRef.current) {
			// Reset layout for all station layers
			const allStationLayers = [
				'stations-vermelho',
				'stations-amarelo',
				'stations-verde',
				'stations-azul',
				'stations-roxo',
			]
			allStationLayers.forEach((layerId) => {
				if (mapRef.current.getLayer(layerId)) {
					mapRef.current.setLayoutProperty(layerId, 'icon-size', 0.7)
					mapRef.current.setLayoutProperty(layerId, 'icon-anchor', 'bottom')
				}
			})
			// Reset layout for route station layers (optional, as they'll be empty)
			const routeLayers = [
				'routeStations-vermelho',
				'routeStations-amarelo',
				'routeStations-verde',
				'routeStations-azul',
				'routeStations-roxo',
				'routeStations-start-finish',
			]
			routeLayers.forEach((layerId) => {
				if (mapRef.current.getLayer(layerId)) {
					mapRef.current.setLayoutProperty(layerId, 'icon-size', 1) // Default route size
					mapRef.current.setLayoutProperty(layerId, 'icon-anchor', 'bottom')
				}
			})

			mapRef.current.setPitch(0) // Reset pitch

			const routeSource = mapRef.current.getSource('routeStations')
			if (routeSource) {
				routeSource.setData({
					type: 'FeatureCollection',
					features: [],
				})
			}

			// Trigger a map refresh/event to ensure changes apply immediately
			mapRef.current.fire('moveend')
		}
		setRouteJustReset(true) // Set flag after reset logic
	}

	return (
		<>
			<Sheet
				open={routeModalOpen}
				onOpenChange={(open) => {
					// Changed param name for clarity
					setRouteModalOpen(open)
					// Reset autocomplete states when closing the sheet
					if (!open) {
						// Check if sheet is closing
						setDepartureQuery('')
						setArrivalQuery('')
						setDepartureOptions([])
						setArrivalOptions([])
						if (!routeJustReset) {
							// Only change URL if not just reset
							const newParams = new URLSearchParams(searchParams)
							newParams.delete('planejar')
							setSearchParams(newParams)
						}
						setRouteJustReset(false) // Always reset the flag after closing
					} else {
						const newParams = new URLSearchParams(searchParams)
						newParams.set('planejar', 'viagem')
						setSearchParams(newParams)
					}
				}}
			>
				<SheetContent
					ref={sheetScrollRef}
					onOpenAutoFocus={(e) => e.preventDefault()}
					className="w-full overflow-y-auto pb-20"
					side={'right'}
				>
					<SheetHeader className="sr-only">
						<SheetTitle>Criar Rota</SheetTitle>
					</SheetHeader>
					{routeStations.length === 0 ? (
						<div className="min-h-full space-y-8 pb-6">
							<div className="flex items-center space-x-2">
								<img
									className="aspect-square h-10 w-10"
									src="/img/logo.webp"
									alt="Logo Carregados"
								/>
								<span className="text-xl font-bold">Planejar Viagem</span>
							</div>
							<div>
								<div className="px-2 font-bold">Saída</div>
								<div className="">
									<AutoComplete
										options={departureOptions || []}
										emptyMessage="Nenhum resultado encontrado."
										placeholder={
											departureValue &&
											departureValue.display_id === 'userLocation'
												? 'Minha localização atual'
												: 'Digite a localização...'
										}
										isLoading={departureFetcher.state === 'loading'}
										onValueChange={(newValue) => {
											setDepartureValue(newValue)
											if (
												error ===
												'Por favor, preencha os locais de saída e chegada.'
											) {
												setError(null)
											}
										}}
										value={departureValue}
										disabled={isDepartureDisabled}
										query={departureQuery}
										setQuery={setDepartureQuery}
										required={true}
										errorMessage="Selecione um local de saída"
										noOptionsMessage="Nenhum local encontrado. Tente outro termo."
										maxSuggestions={5}
										aria={{
											label: 'Local de saída',
											description:
												'Digite ou selecione o local de partida da sua viagem',
										}}
										clearable={!isDepartureDisabled}
									/>
								</div>
								<Button
									variant="ghost"
									className={`flex h-8 items-center gap-1.5 rounded-md px-2 text-sm transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
										departureValue &&
										departureValue.display_id === 'userLocation'
											? 'bg-red-100 text-red-600 hover:bg-red-200' // Active state remains red
											: 'bg-blue-100 text-primary hover:bg-primary/10 hover:text-primary' // Add light blue bg and adjust text color
									}`}
									onClick={() => {
										if (
											departureValue &&
											departureValue.display_id === 'userLocation'
										) {
											setDepartureValue(undefined)
											setDepartureDisabled(false)
											setDepartureQuery('') // Explicitly clear the input text
											if (
												error ===
												'Por favor, preencha os locais de saída e chegada.'
											) {
												setError(null)
											}
											return
										} else if (navigator.geolocation) {
											navigator.geolocation.getCurrentPosition(
												(position) => {
													setDepartureValue({
														lat: position.coords.latitude,
														lon: position.coords.longitude,
														display_name: 'Localização atual',
														display_id: 'userLocation',
														place_id: 'userLocation',
														label: 'Localização atual',
														value: 'Sua localização atual',
														placeId: 'userLocation',
													})
													setDepartureOptions([])
													setDepartureDisabled(true)
													if (
														error ===
														'Por favor, preencha os locais de saída e chegada.'
													) {
														setError(null)
													}
												},
												(err) => {
													console.error('Error getting location:', err)
													setError(
														'Não foi possível obter sua localização. Verifique as permissões de localização.',
													)
												},
												{
													enableHighAccuracy: true,
													timeout: 5000,
													maximumAge: 0,
												},
											)
										} else {
											setError(
												'Geolocalização não é suportada pelo seu navegador.',
											)
										}
									}}
									aria-label={
										departureValue &&
										departureValue.display_id === 'userLocation'
											? 'Remover localização atual'
											: 'Usar minha localização atual'
									}
								>
									{departureValue &&
									departureValue.display_id === 'userLocation' ? (
										<>
											<LocateOff className="h-4 w-4" /> {/* Smaller icon */}
											<span>Remover Localização</span>
										</>
									) : (
										<>
											<Locate className="h-4 w-4" /> {/* Smaller icon */}
											<span>Usar Minha Localização</span> {/* Shorter text */}
										</>
									)}
								</Button>
							</div>
							<div>
								<div className="px-2 font-bold">Chegada</div>
								<AutoComplete
									options={arrivalOptions || []}
									emptyMessage="Nenhum resultado encontrado."
									placeholder="Digite a localização..."
									isLoading={arrivalFetcher.state === 'loading'}
									onValueChange={(newValue) => {
										setArrivalValue(newValue)
										if (
											error ===
											'Por favor, preencha os locais de saída e chegada.'
										) {
											setError(null)
										}
									}}
									value={arrivalValue}
									disabled={isArrivalDisabled}
									query={arrivalQuery}
									setQuery={setArrivalQuery}
									required={true}
									errorMessage="Selecione um local de chegada"
									noOptionsMessage="Nenhum local encontrado. Tente outro termo."
									maxSuggestions={5}
									aria={{
										label: 'Local de chegada',
										description: 'Digite ou selecione o destino da sua viagem',
									}}
								/>
							</div>
							<div className="flex flex-col space-y-2">
								<Tabs
									defaultValue="carro"
									className="w-full"
									onValueChange={(value) => setTab(value)}
								>
									<TabsList className="grid w-full grid-cols-2">
										<TabsTrigger value="carro">Meu Carro</TabsTrigger>
										<TabsTrigger value="manual">Manual</TabsTrigger>
									</TabsList>
									<TabsContent value="carro">
										<Card className="border-0 bg-gray-50 shadow-md">
											<CardContent className="space-y-4 p-5">
												<div className="py-2">
													{userCars.length === 0 ? (
														<div className="flex flex-col items-center justify-center space-y-4 p-6 text-center">
															<span className="text-xl font-semibold text-gray-700">
																Nenhum carro encontrado
															</span>
															<p className="text-sm text-gray-500">
																Adicione um carro à sua garagem para começar a
																planejar suas viagens.
															</p>
															<Link to="/garagem">
																<Button variant="outline" size="sm">
																	Adicionar Carro
																</Button>
															</Link>
														</div>
													) : (
														<div className="flex flex-col space-y-4">
															{selectedCar && (
																<>
																	<div className="flex items-center space-x-4">
																		<div className="flex-shrink-0">
																			<div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
																				<span className="text-2xl font-bold">
																					{selectedCar.userCar.make.charAt(0)}
																				</span>
																			</div>
																		</div>
																		<div>
																			<div className="flex items-center space-x-2">
																				<h3 className="text-lg font-semibold text-gray-800">
																					{selectedCar.userCar.name}
																				</h3>
																				<span className="rounded bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
																					BETA
																				</span>
																			</div>
																			{selectedCar.subtypeName !== 'none' && (
																				<p className="text-sm text-gray-500">
																					{selectedCar.subtypeName}
																				</p>
																			)}
																			<div className="mt-2 flex w-fit items-center space-x-4 rounded-lg bg-gray-50 px-3 py-1.5">
																				<div className="flex items-center">
																					<Battery className="mr-1.5 h-4 w-4 text-primary" />
																					<span className="text-sm text-gray-700">
																						{selectedCar.batteryCapacity} kWh
																					</span>
																				</div>
																				<div className="flex items-center">
																					<Gauge className="mr-1.5 h-4 w-4 text-primary" />
																					<span className="text-sm text-gray-700">
																						{selectedCar.range
																							? `${(selectedCar.range / 1000).toLocaleString('pt-BR')} km`
																							: 'N/A'}
																					</span>
																				</div>
																			</div>
																		</div>
																	</div>
																	<div className="mt-4 space-y-5">
																		<div>
																			<div className="mb-2 flex items-center justify-between px-1">
																				<span className="font-medium text-gray-700">
																					Carga da bateria na saída:
																				</span>
																				<div className="text-right">
																					<div className="font-semibold">
																						{initialBattery}%
																					</div>
																					{selectedCar?.batteryCapacity && (
																						<div className="text-xs text-gray-500">
																							(
																							{Math.round(
																								(selectedCar.batteryCapacity *
																									initialBattery) /
																									100,
																							)}{' '}
																							kWh)
																						</div>
																					)}
																				</div>
																			</div>
																			<Slider
																				defaultValue={initialBattery}
																				max={100}
																				min={10}
																				step={1}
																				onValueChange={setInitialBattery}
																				className="w-full"
																			/>
																		</div>
																		<div>
																			<div className="mb-2 flex items-center justify-between px-1">
																				<span className="font-medium text-gray-700">
																					Carga da bateria na chegada:
																				</span>
																				<div className="text-right">
																					<div className="font-semibold">
																						{arrivalBatteryTarget}%
																					</div>
																					{selectedCar?.batteryCapacity && (
																						<div className="text-xs text-gray-500">
																							(
																							{Math.round(
																								(selectedCar.batteryCapacity *
																									arrivalBatteryTarget) /
																									100,
																							)}{' '}
																							kWh)
																						</div>
																					)}
																				</div>
																			</div>

																			<Slider
																				defaultValue={arrivalBatteryTarget}
																				max={90}
																				min={5}
																				step={1}
																				onValueChange={setArrivalBatteryTarget}
																				className="w-full"
																			/>
																		</div>
																	</div>
																</>
															)}
															{userCars.length > 1 && (
																<Select
																	onValueChange={(value) => {
																		const car = userCars.find(
																			(car) => car.id === value,
																		)
																		if (car) {
																			setSelectedCar(car)
																		}
																	}}
																>
																	<SelectTrigger className="mt-2 w-full border-gray-200">
																		<SelectValue placeholder="Selecionar outro carro..." />
																	</SelectTrigger>
																	<SelectContent className="z-[999999] max-h-[200px] overflow-y-auto">
																		{userCars
																			.sort((a, b) =>
																				a.userCar.name.localeCompare(
																					b.userCar.name,
																				),
																			)
																			.map((car) => (
																				<SelectItem key={car.id} value={car.id}>
																					{car.userCar.name}
																				</SelectItem>
																			))}
																	</SelectContent>
																</Select>
															)}
														</div>
													)}
													<div className="mt-4">
														<Link
															to="/garagem"
															className="flex items-center text-sm transition-colors hover:text-primary/80 hover:underline"
														>
															<Building className="mr-1.5 h-4 w-4" />
															Ir para garagem
														</Link>
													</div>
												</div>
											</CardContent>
										</Card>
									</TabsContent>
									<TabsContent value="manual">
										<Card className="border-0 bg-gray-50 shadow-md">
											<CardContent className="space-y-5 p-5">
												<div className="space-y-2">
													<div className="mb-2 flex items-center justify-between px-1">
														<span className="font-medium text-gray-700">
															Autonomia com 100% de carga:
														</span>
														<span className="font-semibold">{range[0]} km</span>
													</div>
													<Slider
														defaultValue={range}
														max={1000}
														min={10}
														step={10}
														onValueChange={setRange}
														className="w-full"
													/>
												</div>
												<div className="space-y-2">
													<div className="mb-2 flex items-center justify-between px-1">
														<span className="font-medium text-gray-700">
															Capacidade da bateria:
														</span>
														<span className="font-semibold">
															{batteryCapacity} kWh
														</span>
													</div>
													<Slider
														defaultValue={batteryCapacity}
														max={400}
														min={10}
														step={1}
														onValueChange={setBatteryCapacity}
														className="w-full"
													/>
												</div>
												<div className="space-y-2">
													<div className="mb-2 flex items-center justify-between px-1">
														<span className="font-medium text-gray-700">
															Carga da bateria na saída:
														</span>
														<span className="font-semibold">
															{initialBattery} %
														</span>
													</div>
													<Slider
														defaultValue={initialBattery}
														max={100}
														min={10}
														step={1}
														onValueChange={setInitialBattery}
														className="w-full"
													/>
												</div>
												<div className="space-y-2 pb-2">
													<div className="mb-2 flex items-center justify-between px-1">
														<span className="font-medium text-gray-700">
															Carga da bateria na chegada:
														</span>
														<span className="font-semibold">
															{arrivalBatteryTarget} %
														</span>
													</div>
													<Slider
														defaultValue={arrivalBatteryTarget}
														max={90}
														min={5}
														step={1}
														onValueChange={setArrivalBatteryTarget}
														className="w-full"
													/>
												</div>
											</CardContent>
										</Card>
									</TabsContent>
								</Tabs>
								{/* Container for the conditionally shown elements with fade transition */}
								<div className="relative min-h-[80px] pt-4">
									{/* Progress Indicator Block */}
									{isProgressMounted && (
										<div
											className={`absolute inset-x-0 top-4 flex w-full flex-col items-center space-y-3 transition-opacity duration-300 ease-in-out ${
												isProgressVisible ? 'opacity-100' : 'opacity-0'
											}`}
										>
											{/* Spinner */}
											<svg
												className="h-6 w-6 animate-spin text-blue-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"
												/>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											{/* Progress Bar */}
											<div className="relative w-full px-4">
												<Progress
													value={routeProgress}
													className="h-3 w-full rounded-full bg-gray-200 shadow-inner" // Updated styling
												/>
											</div>
											{/* Progress Text with Percentage */}
											<p className="text-center text-sm font-medium text-gray-700">
												{routeProgressText} {Math.round(routeProgress)}%
											</p>
										</div>
									)}

									{/* Button/Error Block */}
									{isButtonBlockMounted && (
										<div
											className={`absolute inset-x-0 top-4 flex w-full flex-col items-center transition-opacity duration-300 ease-in-out ${
												isButtonBlockVisible ? 'opacity-100' : 'opacity-0'
											}`}
										>
											{error && (
												<p className="mb-3 text-center font-bold text-red-500">
													{error}
												</p>
											)}
											{tab === 'manual' || selectedCar ? (
												<Button onClick={handleCreateRoute} className="w-full">
													Buscar
												</Button>
											) : (
												// If no error and no button, render a placeholder to prevent collapse
												!error && <div className="h-10 w-full" /> // Approx button height
											)}
										</div>
									)}
								</div>
							</div>
						</div>
					) : (
						<Trip
							routeStations={routeStations}
							routeTotalTime={routeTotalTime}
							routeTotalDistance={routeTotalDistance}
							routeTotalChargingTime={routeTotalChargingTime}
							routeTotalDrivingTime={routeTotalDrivingTime}
							routeId={routeId}
							resetRoute={resetRoute} // Pass the new function
							directionsRef={directionsRef}
							mapRef={mapRef}
							routeType={
								departureValue && departureValue.display_id === 'userLocation'
									? 'user'
									: 'route'
							}
							blockedStations={blockedStations}
							setBlockedStations={setBlockedStations} // Keep this one if needed for blocking *during* route display
							routeFetcherState={routeFetcher.state}
						/>
					)}
				</SheetContent>
			</Sheet>
			<div id="full" className="h-full w-full">
				<div className="relative h-full w-full overflow-clip">
					<div ref={mapContainer} className="absolute h-full w-full" />
					{fetcher.state !== 'idle' && (
						// Subtle loading indicator
						<div className="absolute bottom-2 right-2 z-10 rounded-full bg-white/80 p-1 shadow backdrop-blur-sm transition-opacity duration-300 ease-in-out">
							<Loader2 className="h-4 w-4 animate-spin text-gray-500" />
						</div>
					)}
				</div>
			</div>
		</>
	)
}
